import React, { useEffect, useState } from "react";
import axios from "axios";
import { createScenarioData, updateScenarioData } from "../api/scenarioData";
import { formatTreatmentNumber, TreatmentIdentity } from "./TreatmentPresentation";

const emptyForm = {
  treatment: "", cost_type: "INITIAL", currency: "", amount: "",
  start_year: 0, duration_months: "", is_estimate: false, notes: "",
};
const costTypes = [
  ["INITIAL", "Initial"], ["IMPLEMENTATION", "Implementation"],
  ["ANNUAL", "Annual recurring"], ["MONTHLY", "Monthly recurring"],
  ["DECOMMISSION", "Decommission"], ["VARIABLE", "Variable"],
  ["PREMIUM", "Insurance premium"], ["RESERVE", "Capital reserve"],
];

export default function TreatmentCostManager() {
  const [costs, setCosts] = useState([]);
  const [options, setOptions] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [message, setMessage] = useState(null);

  const load = async () => {
    try {
      const [costResponse, optionResponse] = await Promise.all([
        axios.get("/api/digital-risk/treatment-costs/"),
        axios.get("/api/digital-risk/treatment-options/"),
      ]);
      setCosts(costResponse.data);
      setOptions(optionResponse.data);
    } catch (error) {
      setMessage({ type: "error", text: "Unable to load treatment costs." });
    }
  };
  useEffect(() => { load(); }, []);
  const change = (field) => (event) => setForm({
    ...form,
    [field]: event.target.type === "checkbox" ? event.target.checked : event.target.value,
  });
  const reset = () => { setForm(emptyForm); setEditingId(null); setIsFormOpen(false); };

  const save = async () => {
    const amount = Number(form.amount);
    if (!form.treatment || !form.currency.trim() || form.currency.trim().length !== 3 || form.amount === "" || amount < 0) {
      setMessage({ type: "error", text: "Treatment Option, a three-character currency, and a non-negative amount are required." });
      return;
    }
    const payload = {
      ...form,
      treatment: Number(form.treatment),
      amount: form.amount,
      start_year: Number(form.start_year),
      duration_months: form.duration_months === "" ? null : Number(form.duration_months),
    };
    try {
      if (editingId) await updateScenarioData("treatmentCosts", editingId, payload);
      else await createScenarioData("treatmentCosts", payload);
      setMessage({ type: "success", text: `Treatment cost ${editingId ? "updated" : "created"} successfully.` });
      reset();
      await load();
    } catch (error) {
      setMessage({ type: "error", text: "Unable to save treatment cost." });
    }
  };
  const edit = (cost) => {
    setIsFormOpen(true);
    setEditingId(cost.id);
    setForm(Object.fromEntries(Object.keys(emptyForm).map((key) => [key, cost[key] ?? emptyForm[key]])));
  };

  return <div className="p-6 flex flex-col gap-6">
    {message && <div role="alert" className={`px-4 py-3 rounded-xl text-sm font-medium ${message.type === "success" ? "bg-green-100 text-green-800 border border-green-200" : "bg-red-100 text-red-800 border border-red-200"}`}>{message.text}</div>}
    <div className="bg-white border rounded-2xl p-5 overflow-x-auto">
      <div className="catalogue-header flex items-center justify-between gap-4 mb-4">
        <h2 className="font-semibold">Treatment Costs</h2>
        <button type="button" aria-expanded={isFormOpen} aria-controls="treatment-cost-form" onClick={() => { if (isFormOpen) reset(); else setIsFormOpen(true); }} className="border border-slate-300 bg-white px-4 py-2 rounded-xl font-medium hover:bg-slate-50">+ New Treatment Cost</button>
      </div>
      {isFormOpen && <div id="treatment-cost-form" className="bg-white border rounded-2xl p-5 space-y-4">
      <div><h2 className="font-semibold">{editingId ? "Edit Treatment Cost" : "New Treatment Cost"}</h2><p className="text-sm text-slate-500 mt-1">Maintain modeled economic inputs associated with Treatment Options for analytical decisions. They are not approved budgets, authorizations, commitments, actual expenditure, invoices, or realized benefits.</p></div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Select label="Treatment Option" value={form.treatment} onChange={change("treatment")} options={options.map((option) => [option.id, `${option.code} — ${option.name}`])} placeholder="Select a Treatment Option" />
        <Select label="Cost type" value={form.cost_type} onChange={change("cost_type")} options={costTypes} />
        <label className="text-sm">Currency *<input aria-label="Currency" maxLength="3" value={form.currency} onChange={change("currency")} className="block w-full border rounded-xl px-3 py-2 mt-1" /></label>
        <label className="text-sm">Amount *<input aria-label="Amount" type="number" min="0" step="0.01" value={form.amount} onChange={change("amount")} className="block w-full border rounded-xl px-3 py-2 mt-1" /></label>
        <label className="text-sm">Start year<input aria-label="Start year" type="number" min="0" value={form.start_year} onChange={change("start_year")} className="block w-full border rounded-xl px-3 py-2 mt-1" /></label>
        <label className="text-sm">Duration months<input aria-label="Duration months" type="number" min="0" value={form.duration_months} onChange={change("duration_months")} className="block w-full border rounded-xl px-3 py-2 mt-1" /></label>
        <label className="text-sm flex items-center gap-2"><input aria-label="Estimate" type="checkbox" checked={form.is_estimate} onChange={change("is_estimate")} />Estimate</label>
        <label className="text-sm md:col-span-3">Notes<textarea aria-label="Notes" value={form.notes} onChange={change("notes")} className="block w-full border rounded-xl px-3 py-2 mt-1" /></label>
      </div>
      <p className="text-xs text-slate-500">Currency is the denomination of each modeled cost input. RIGO does not infer comparability or perform foreign-exchange conversion between Treatment Cost records.</p>
      <div className="flex gap-3"><button type="button" onClick={save} className="bg-slate-900 text-white px-5 py-3 rounded-xl">{editingId ? "Update" : "Create"}</button><button type="button" onClick={reset} className="border px-5 py-3 rounded-xl">Cancel</button></div>
    </div>}
      <table className="w-full text-sm"><thead><tr className="border-b text-gray-500">{["Treatment Option", "Cost type", "Amount", "Start year", "Duration", "Estimate", "Notes", "Actions"].map((heading) => <th key={heading} className="text-left py-3 pr-4">{heading}</th>)}</tr></thead><tbody>{costs.map((cost) => <tr key={cost.id} className="border-b align-top"><td className="py-3 pr-4"><TreatmentIdentity name={cost.treatment_name} code={cost.treatment_code} /></td><td className="py-3 pr-4">{costTypes.find(([value]) => value === cost.cost_type)?.[1]}</td><td className="py-3 pr-4 whitespace-nowrap">{cost.currency} {formatTreatmentNumber(cost.amount)}</td><td className="py-3 pr-4">{cost.start_year}</td><td className="py-3 pr-4">{cost.duration_months == null ? "—" : `${cost.duration_months} months`}</td><td className="py-3 pr-4">{cost.is_estimate ? "Yes" : "No"}</td><td className="py-3 pr-4 max-w-xs whitespace-normal">{cost.notes || "—"}</td><td className="py-3 text-right"><button onClick={() => edit(cost)} className="text-slate-700 hover:text-black">Edit</button></td></tr>)}{costs.length === 0 && <tr><td colSpan="8" className="py-6 text-center text-gray-400">No treatment costs found.</td></tr>}</tbody></table>
    </div>
  </div>;
}

function Select({ label, value, onChange, options, placeholder }) {
  return <label className="text-sm">{label}<select aria-label={label} value={value} onChange={onChange} className="block w-full border rounded-xl px-3 py-2 mt-1">{placeholder && <option value="">{placeholder}</option>}{options.map(([itemValue, name]) => <option key={itemValue} value={itemValue}>{name}</option>)}</select></label>;
}
